import { Select, SelectItem, Text } from "@ui-kitten/components";
import {
  AccountTicketDetails,
  EventDetails,
  PlanPackageComponentDetails,
  SubscriptionDetails,
  TicketCartDetails,
  TicketDetails,
} from "features/types";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { View } from "react-native";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import toast from "utils/toast";
import {
  componentNotExceedingPerEventMax,
  findComponentById,
  findUserById,
  getRemainingPerEventMaxForComponent,
  getRemainingQuantityForComponent,
  isPrimaryComponent,
} from "../helpers";
import { useAccount } from "hooks/useAccount";
import { MAX_GUEST_SEATS } from "constants/";
import i18n from "plugins/localization";
import { Image } from "expo-image";
import { useAppSelector } from "hooks/useAppSelector";
import { getUserName } from "utils/userUtils";
import { getPlanType } from "utils/planPackageUtils";
import { formatCurrency } from "utils/appUtils";

interface HoldDetailsProps {
  ticket: TicketDetails;
  planType: PlanPackageComponentDetails["type"];
  charge: TicketCartDetails | undefined;
  matchedSubscriptions: SubscriptionDetails[];
  initialIndex: number;
  selectedUsersAndComponents: number[];
  addSelectedUserOrComponent: (id: number, initialIndex: number) => void;
  guestComponents: PlanPackageComponentDetails[];
  prefillSelectorArray: number[];
  selectedSubscription: SubscriptionDetails;
}

export const RenderCardTitle = ({
  isGA,
  index,
  charge,
  ticketPrice,
}: {
  isGA: boolean;
  index: number;
  charge?: TicketCartDetails;
  ticketPrice: string | number;
}) => (
  <View
    style={{
      flexDirection: "row",
      justifyContent: "space-between",
      display: "flex",
      width: "100%",
    }}
  >
    <Text>
      {isGA ? "GA" : "Seat"} {index}
    </Text>
    <View>
      {charge ? (
        <Text category="s1">{ticketPrice}</Text>
      ) : (
        <Text>loading...</Text>
      )}
    </View>
  </View>
);

export const FanRallyMarketPlace = ({ name }: { name: string }) => (
  <View
    style={{
      marginTop: 10,
      flexDirection: "row",
      alignItems: "center",
    }}
  >
    <Image
      source={{
        uri: "https://branding.fanrally.com/logos/fanrally_logo.svg",
      }}
      style={{
        width: 50,
        height: 35,
      }}
      contentFit="contain"
      responsivePolicy="initial"
    />

    <Text category="s1">{name}</Text>
  </View>
);

const HoldDetails: React.FC<HoldDetailsProps> = ({
  ticket,
  charge,
  planType,
  matchedSubscriptions,
  initialIndex,
  selectedUsersAndComponents,
  addSelectedUserOrComponent,
  guestComponents,
  prefillSelectorArray,
  selectedSubscription,
}) => {
  const { accountEvents: events } = useAccount();
  const { isChangingSeats, originalEventId } = useAppSelector(
    (state) => state.reservationFlow
  );
  const { seat, id: ticketId, event } = ticket;
  const [selectedUserOrComponentId, setSelectedUserOrComponentId] =
    useState<number>();
  const [computedMembers, setComputedMembers] = useState<SubscriptionDetails[]>(
    []
  );
  const selectedMember = computedMembers.find(
    (sub) => sub.member.user.id === selectedUserOrComponentId
  )?.member;
  const displayValue = selectedMember
    ? getUserName(selectedMember)
    : findComponentById(selectedUserOrComponentId || 0, matchedSubscriptions)
        ?.name;

  // returns true if there is only one user member in the selectedUsersAndComponents array
  const oneUserSelected =
    selectedUsersAndComponents.filter((userOrComponent) => {
      return findUserById(userOrComponent, matchedSubscriptions) !== undefined;
    }).length === 1;

  // returns true if there is only one primary voucher component selected among the selectors
  const onePrimaryComponentSelected = useMemo(
    () =>
      selectedUsersAndComponents.filter((component) =>
        isPrimaryComponent(component, selectedSubscription)
      ).length === 1,
    [selectedUsersAndComponents]
  );

  // Returns true if user is changing the value of the selector from a guest component
  // to another guest component
  const switchingGuest = useCallback(
    (newComponent: PlanPackageComponentDetails) => {
      if (!selectedUserOrComponentId) return false;
      // the current selected id is an user id
      if (findUserById(selectedUserOrComponentId, matchedSubscriptions))
        return false;
      // the current selected id is a primary component id
      if (isPrimaryComponent(selectedUserOrComponentId, selectedSubscription)) {
        return false;
      }
      return newComponent.type.includes("GUEST");
    },
    [selectedUserOrComponentId, selectedSubscription]
  );

  const getTicketChargeByTicketId = (ticketId: number) => {
    if (!charge) return 0;

    const { allInclusivePrice } = selectedSubscription.planPackage;
    const { items, feeItems } = charge;
    let totalCharge = 0;

    const chargeItem = items.find((item) => item.ticketId === ticketId);
    totalCharge = Number(chargeItem?.totalPrice);

    if (allInclusivePrice && feeItems && feeItems.length > 0) {
      feeItems
        .filter((item) => item.ticketId === ticketId)
        .forEach((item) => {
          totalCharge += Number(item?.totalPrice);
        });
    }

    return totalCharge;
  };

  // retuns undefined if selected subscripiton is RESERVATION
  const component = findComponentById(
    selectedUserOrComponentId || 0,
    matchedSubscriptions
  );

  const componentHasNotReachedMax = (
    component: PlanPackageComponentDetails,
    isChangingSeats: boolean = false
  ) => {
    const maxForComponent = getRemainingPerEventMaxForComponent({
      component,
      subscriptions: matchedSubscriptions,
      events,
      eventId: event.id,
      isChangingSeats,
    });
    const remainingQuantityForComponent = getRemainingQuantityForComponent({
      component,
      subscriptions: matchedSubscriptions,
      events: events || [],
      isChangingSeats,
      originalEventId,
      eventId: event.id,
    });
    const componentAlreadySelectedAmount = selectedUsersAndComponents.filter(
      (id) => id === component.id
    ).length;

    return (
      componentAlreadySelectedAmount < maxForComponent &&
      componentAlreadySelectedAmount < remainingQuantityForComponent &&
      componentNotExceedingPerEventMax({
        component,
        accountEvents: events,
        selectedEvent: ticket.event,
        isChangingSeats,
      })
    );
  };

  const selectedPrimaryVouchersNotExceedingPerEventMax = ({
    primaryComponent,
    accountEvents = [],
    selectedEvent,
    isChangingSeats = false,
  }: {
    primaryComponent: PlanPackageComponentDetails;
    accountEvents?: AccountTicketDetails[];
    selectedEvent: EventDetails;
    isChangingSeats?: boolean;
  }) => {
    const voucherPerEventMax = matchedSubscriptions
      .filter(
        (sub) => sub.planPackage.primaryComponent.id === primaryComponent.id
      ) // find matching subscriptions first
      .reduce((result: number, currentSub) => {
        const {
          planPackage: {
            primaryComponent: { perEventMax },
          },
        } = currentSub;
        if (perEventMax === -1) return result + MAX_GUEST_SEATS;
        return currentSub.planPackage.primaryComponent.perEventMax + result;
      }, 0); // add all primary components' per event max
    const remainingQuantity = getRemainingQuantityForComponent({
      component: primaryComponent,
      subscriptions: matchedSubscriptions,
      events: events || [],
      isChangingSeats,
      originalEventId,
      eventId: selectedEvent.id,
    });

    const totalPrimaryComponentUsed = accountEvents // find the amount of all primary vouchers used for the current event
      .filter((event) => event.event.id === selectedEvent.id)
      .reduce((result: number, currentAccountEvent) => {
        let primaryComponentUsedForEvent = 0;
        for (let ticket of currentAccountEvent.tickets) {
          if (
            !isChangingSeats &&
            ticket.planPackageComponentId === primaryComponent.id
          ) {
            primaryComponentUsedForEvent++;
          }
        }
        return primaryComponentUsedForEvent + result;
      }, 0);

    // find the amount of all primary voucher selected during current reservation
    const selectedPrimaryComponentIdCount = selectedUsersAndComponents.filter(
      (id) => id === primaryComponent.id
    ).length;
    return (
      selectedPrimaryComponentIdCount + totalPrimaryComponentUsed <
      // per event max cannot exceed the remaining quantity(available vouchers)
      Math.min(voucherPerEventMax, remainingQuantity)
    );
  };

  const componentsHaveNotReachedMax = guestComponents.filter((component) =>
    componentHasNotReachedMax(component, isChangingSeats)
  );

  // show unselected members and current selected member in select items
  useEffect(() => {
    const newComputedMembers = matchedSubscriptions.filter(
      ({ member: { user } }) => {
        return (
          !selectedUsersAndComponents.includes(user.id) ||
          user.id === selectedUserOrComponentId
        );
      }
    );

    setComputedMembers(newComputedMembers);
  }, [selectedUsersAndComponents, selectedUserOrComponentId]);

  // pre fill selectors
  useEffect(() => {
    if (matchedSubscriptions.length === 0) return;
    if (planType === "RESERVATION") {
      const newSelectedUserId =
        matchedSubscriptions[initialIndex]?.member.user.id;
      if (newSelectedUserId) {
        // if the selector array at initialIndex is a user id,
        // fill the current select dropdown with the user id
        setSelectedUserOrComponentId(newSelectedUserId);
        addSelectedUserOrComponent(newSelectedUserId, initialIndex);
        return;
      }
    }
    // if the selector array at initialIndex is a component id
    // fill the current select dropdown with the component id
    setSelectedUserOrComponentId(prefillSelectorArray[initialIndex]);
    addSelectedUserOrComponent(
      prefillSelectorArray[initialIndex],
      initialIndex
    );
  }, [matchedSubscriptions]);

  // returns true if selected subscription is RESERVATION and selectedUserOrComponentId is user id
  const isReservationAndNotGuestComponent = useMemo(
    () =>
      getPlanType(selectedSubscription.planPackage) === "RESERVATION" &&
      component === undefined,
    [component]
  );

  // returns true if selected subscription is VOUCHER and selectedUserOrComponentId is the primary component id
  const isVoucherAndIsPrimary = useMemo(
    () =>
      getPlanType(selectedSubscription.planPackage) === "VOUCHER" &&
      component?.primary,
    [component]
  );

  return (
    <View style={{ marginBottom: 10 }}>
      <BaseCard
        title={
          <RenderCardTitle
            isGA={seat.ga}
            index={initialIndex + 1}
            charge={charge}
            ticketPrice={formatCurrency(getTicketChargeByTicketId(ticket.id), {
              priceType: charge?.priceType,
            })}
          />
        }
      >
        <BaseCardListItem
          dividerStyle={{ marginVertical: 8 }}
          title={
            seat.ga
              ? i18n.t("common.generalAdmission")
              : `${i18n.t("common.section")}: ${seat.sectionDisplay} ` +
                `${i18n.t("common.row")}: ${seat.rowDisplay} ` +
                `${i18n.t("common.seat", { count: 1 })}: ${seat.seatDisplay}`
          }
          showArrow={false}
        ></BaseCardListItem>
        {Number(getTicketChargeByTicketId(ticketId)) > 0 &&
          (isReservationAndNotGuestComponent || isVoucherAndIsPrimary) && (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginVertical: 4,
              }}
            >
              <Image
                source={require("assets/images/seat-upgrade-icon.png")}
                style={{ width: 25, height: 25 }}
                responsivePolicy="initial"
              ></Image>
              <Text>
                Seat Upgrade.{" "}
                {isReservationAndNotGuestComponent
                  ? (i18n.t("screens.Reserve.passApplied") as string)
                  : isVoucherAndIsPrimary
                  ? (i18n.t("screens.Reserve.seatCreditApplied") as string)
                  : ""}
              </Text>
            </View>
          )}
        {(planType === "RESERVATION" || guestComponents.length > 0) && (
          <Select
            testID="reserve-user-selector"
            value={displayValue}
            onSelect={() => {}}
            placeholder={i18n.t("screens.Reserve.selectGuestType")}
          >
            <>
              {planType === "RESERVATION" ? (
                computedMembers.map(({ member }, index) => (
                  <SelectItem
                    testID="reserve-user-selector-item"
                    key={member.id}
                    title={getUserName(member)}
                    onPressIn={() => {
                      const newUserId = computedMembers[index]?.member.user.id;
                      if (newUserId) {
                        addSelectedUserOrComponent(newUserId, initialIndex);
                        setSelectedUserOrComponentId(newUserId);
                      }
                    }}
                  ></SelectItem>
                ))
              ) : (
                // if vouchers used for selected event have not exceeded the per event max combined for component
                <>
                  {componentNotExceedingPerEventMax({
                    component:
                      selectedSubscription.planPackage.primaryComponent,
                    accountEvents: events,
                    selectedEvent: event,
                    isChangingSeats,
                  }) &&
                  selectedPrimaryVouchersNotExceedingPerEventMax({
                    primaryComponent:
                      selectedSubscription.planPackage.primaryComponent,
                    accountEvents: events,
                    selectedEvent: event,
                    isChangingSeats,
                  }) ? (
                    <SelectItem
                      testID="reserve-user-selector-item"
                      title={
                        selectedSubscription.planPackage.primaryComponent.name
                      }
                      onPressIn={() => {
                        const primaryComponentId =
                          selectedSubscription.planPackage.primaryComponent.id;
                        addSelectedUserOrComponent(
                          primaryComponentId,
                          initialIndex
                        );
                        setSelectedUserOrComponentId(primaryComponentId);
                      }}
                    ></SelectItem>
                  ) : null}
                </>
              )}
              {componentsHaveNotReachedMax.map((component, index) => (
                <SelectItem
                  title={component.name}
                  key={index}
                  onPressIn={() => {
                    const { id } = component;
                    // check if current selector is a user id
                    const isUserSelected =
                      findUserById(
                        selectedUserOrComponentId!,
                        matchedSubscriptions
                      ) !== undefined;
                    // if user is not switching guest components,
                    // i.e. from paid guest -> included guest
                    // or from included guest -> paid guest
                    if (!switchingGuest(component)) {
                      if (id === selectedUserOrComponentId) return; // if user selected the same item
                      // if user is trying to switch to guest pass from a user, check how many users are selected,
                      // if only one user is selected, then we don't allow it
                      if (
                        (isUserSelected && oneUserSelected) ||
                        onePrimaryComponentSelected
                      ) {
                        toast.error(
                          i18n.t(
                            "screens.Reserve.mustHaveOneAccountMemberAttending"
                          )
                        );
                        return;
                      }
                    }
                    addSelectedUserOrComponent(id, initialIndex);
                    setSelectedUserOrComponentId(id);
                  }}
                ></SelectItem>
              ))}
            </>
          </Select>
        )}
      </BaseCard>
    </View>
  );
};

export default HoldDetails;
